import { useCallback, useEffect, useRef, useState } from 'react';

import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTranslation } from 'react-i18next';

import { PERSISTENT_KEY_MY_PARTICIPANTS_TOUR_PROMPT } from 'src/common/constants/persistentStorageKeys';
import { buildMyParticipantsTourSteps } from 'src/helpers/myParticipantsTour';

type MyParticipantsTourPromptAnswer = 'declined' | 'accepted';

function readMyParticipantsTourPromptAnswer(): MyParticipantsTourPromptAnswer | null {
  const value = localStorage.getItem(PERSISTENT_KEY_MY_PARTICIPANTS_TOUR_PROMPT);
  if (value === 'declined' || value === 'accepted') {
    return value;
  }
  return null;
}

export function writeMyParticipantsTourPromptAnswer(value: MyParticipantsTourPromptAnswer) {
  localStorage.setItem(PERSISTENT_KEY_MY_PARTICIPANTS_TOUR_PROMPT, value);
}

export function clearMyParticipantsTourPromptAnswerForTests() {
  localStorage.removeItem(PERSISTENT_KEY_MY_PARTICIPANTS_TOUR_PROMPT);
}

type Options = {
  ready: boolean;
  showRegisterStep?: boolean;
  showQrStep?: boolean;
};

export function useMyParticipantsTour({
  ready,
  showRegisterStep = false,
  showQrStep = false,
}: Options) {
  const { t } = useTranslation();
  const [offerOpen, setOfferOpen] = useState(false);
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  const offerShownRef = useRef(false);

  const buildSteps = useCallback(
    () => buildMyParticipantsTourSteps(t, { showRegisterStep, showQrStep }),
    [showRegisterStep, showQrStep, t],
  );

  const startTour = useCallback(() => {
    driverRef.current?.destroy();

    const tourDriver = driver({
      showProgress: true,
      allowClose: true,
      overlayOpacity: 0.5,
      smoothScroll: true,
      nextBtnText: t('participants.tour.buttons.next'),
      prevBtnText: t('participants.tour.buttons.back'),
      doneBtnText: t('participants.tour.buttons.done'),
      steps: buildSteps(),
      onDestroyed: () => {
        if (driverRef.current === tourDriver) {
          driverRef.current = null;
        }
      },
    });

    driverRef.current = tourDriver;
    tourDriver.drive();
  }, [buildSteps, t]);

  useEffect(() => {
    if (!ready || offerShownRef.current) {
      return;
    }

    if (readMyParticipantsTourPromptAnswer() !== null) {
      return;
    }

    offerShownRef.current = true;
    setOfferOpen(true);
  }, [ready]);

  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, []);

  const handleOfferDecline = useCallback(() => {
    writeMyParticipantsTourPromptAnswer('declined');
    setOfferOpen(false);
  }, []);

  const handleOfferStart = useCallback(() => {
    writeMyParticipantsTourPromptAnswer('accepted');
    setOfferOpen(false);
    startTour();
  }, [startTour]);

  return {
    offerOpen,
    startTour,
    handleOfferDecline,
    handleOfferStart,
  };
}
